class SchoolSearch extends HTMLElement {
  constructor() {
    super();

    this._onStateChange = this._onStateChange.bind(this);
    this._onCityChange = this._onCityChange.bind(this);
    this._onTypeChange = this._onTypeChange.bind(this);

    this._data = [];
    this._states = [];
    this._citiesByState = new Map();

    // Used to cancel stale searches
    this._searchToken = 0;
  }

  connectedCallback() {
    this.selectState = this.querySelector("#school-search-state");
    this.selectCity = this.querySelector("#school-search-city");
    this.selectType = this.querySelector("#school-search-type");

    this.message = this.querySelector(".school-search__message");
    this.countEl = this.querySelector("#school-search-count");
    this.resultsEl = this.querySelector("#school-search-results");

    if (!this.selectState || !this.selectCity || !this.selectType || !this.message || !this.countEl || !this.resultsEl) return;

    const url = window.__GRADSHOP_SCHOOL_JSON_URL__;
    if (!url) {
      this._setMessage("Unable to load school data.", "error");
      return;
    }

    this._clearResults();
    this._resetCity();
    this._setMessage("Loading schools, please wait…", "loading");

    this._loadData(url);

    this.selectState.addEventListener("change", this._onStateChange);
    this.selectCity.addEventListener("change", this._onCityChange);
    this.selectType.addEventListener("change", this._onTypeChange);
  }

  disconnectedCallback() {
    this.selectState?.removeEventListener("change", this._onStateChange);
    this.selectCity?.removeEventListener("change", this._onCityChange);
    this.selectType?.removeEventListener("change", this._onTypeChange);
  }

  /* ============================
   * DATA LOADING
   * ============================ */

  async _loadData(url) {
    try {
      const res = await fetch(url, { cache: "no-store" });
      const data = await res.json();

      this._data = Array.isArray(data) ? data : [];
      this._buildIndex();
      this._fillStates();

      this._setMessage("Select a state to begin, please.", "info");
    } catch {
      this._data = [];
      this._setMessage("Unable to load school data.", "error");
    }
  }

  _buildIndex() {
    const states = new Set();
    const citiesByState = new Map();

    for (const row of this._data) {
      const state = String(row.state || "").trim();
      const city = String(row.city || "").trim();

      if (!state || !city) continue;

      states.add(state);

      if (!citiesByState.has(state)) {
        citiesByState.set(state, new Set());
      }
      citiesByState.get(state).add(city);
    }

    this._states = Array.from(states).sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));

    this._citiesByState = new Map(Array.from(citiesByState.entries()).map(([state, set]) => [state, Array.from(set).sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }))]));
  }

  /* ============================
   * UI HELPERS
   * ============================ */

  _setMessage(text, status) {
    this.message.textContent = text || "";

    this.message.classList.remove("is-info", "is-loading", "is-success", "is-error");

    if (status === "loading") this.message.classList.add("is-loading");
    else if (status === "success") this.message.classList.add("is-success");
    else if (status === "error") this.message.classList.add("is-error");
    else this.message.classList.add("is-info");
  }

  _setOptions(select, values, placeholder) {
    select.innerHTML = "";
    select.append(new Option(placeholder, ""));
    for (const v of values) {
      select.append(new Option(v, v));
    }
  }

  _fillStates() {
    this._setOptions(this.selectState, this._states, "STATE");
    this._resetCity();
  }

  _resetCity() {
    this._setOptions(this.selectCity, [], "CITY");
    this.selectCity.disabled = true;
  }

  _clearResults() {
    this.countEl.innerHTML = "";
    this.resultsEl.innerHTML = "";
  }

  _renderResults(items) {
    this._clearResults();

    const count = items.length;
    const SCHOOL_LINK_ICON = `
      <svg width="24px" height="24px" viewBox="0 0 24 24" stroke-width="1.5" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000"><path d="M6.00005 19L19 5.99996M19 5.99996V18.48M19 5.99996H6.52005" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>
    `;

    if (count === 0) {
      this.countEl.innerHTML = '<p class="h4">No results found.</p>';
    } else if (count === 1) {
      this.countEl.innerHTML = '<p class="h4">1 result</p>';
    } else {
      this.countEl.innerHTML = `<p class="h4">${count} results</p>`;
    }

    for (const row of items) {
      const li = document.createElement("li");

      const a = document.createElement("a");
      a.className = "link list-menu__item--link school-search__link";
      a.href = row.handle;
      a.target = "_blank";
      a.rel = "noopener noreferrer";

      // Text node first (better accessibility)
      const text = document.createTextNode(row.title);
      a.appendChild(text);

      // Icon (inline SVG)
      a.insertAdjacentHTML("beforeend", SCHOOL_LINK_ICON);

      li.appendChild(a);
      this.resultsEl.appendChild(li);
    }
  }

  /* ============================
   * SEARCH LOGIC
   * ============================ */

  _getSelection() {
    return {
      state: this.selectState.value,
      city: this.selectCity.value,
      type: this.selectType.value,
    };
  }

  _filter(sel) {
    if (!sel.state || !sel.city || !sel.type) return [];

    return this._data.filter((row) => String(row.state) === sel.state && String(row.city) === sel.city && String(row.type) === sel.type);
  }

  _sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async _withLoading(run) {
    const token = ++this._searchToken;

    this._setMessage("Searching, please wait…", "loading");

    // Allow spinner to render
    await Promise.resolve();

    // Minimum spinner duration to avoid flashing
    const minMs = 120;
    const start = performance.now();

    const result = await run();

    const elapsed = performance.now() - start;
    if (elapsed < minMs) {
      await this._sleep(minMs - elapsed);
    }

    if (token !== this._searchToken) return null;
    return result;
  }

  async _refresh() {
    const sel = this._getSelection();

    if (!sel.state) {
      this._setMessage("Select a state to begin, please.", "info");
      this._clearResults();
      return;
    }

    if (!sel.city && !sel.type) {
      this._setMessage("Please select a city and school type.", "info");
      this._clearResults();
      return;
    }

    if (!sel.city) {
      this._setMessage("Please select a city.", "info");
      this._clearResults();
      return;
    }

    if (!sel.type) {
      this._setMessage("Please select a school type.", "info");
      this._clearResults();
      return;
    }

    const items = await this._withLoading(() => this._filter(sel));
    if (!items) return;

    this._renderResults(items);

    if (items.length > 0) {
      this._setMessage("Schools found for this selection.", "success");
    } else {
      this._setMessage("No schools found for this selection, please try another option.", "error");
    }
  }

  /* ============================
   * EVENTS
   * ============================ */

  _onStateChange() {
    this._searchToken++;
    this._clearResults();

    const state = this.selectState.value;

    if (!state) {
      this._resetCity();
      this._setMessage("Select a state to begin, please.", "info");
      return;
    }

    const cities = this._citiesByState.get(state) || [];
    this._setOptions(this.selectCity, cities, "CITY");
    this.selectCity.disabled = false;

    this._setMessage("Please select a city and school type.", "info");
    this._refresh();
  }

  _onCityChange() {
    this._searchToken++;

    if (this.selectState.value && this.selectCity.value && !this.selectType.value) {
      this._setMessage("Please select a school type.", "info");
      this._clearResults();
      return;
    }

    this._refresh();
  }

  _onTypeChange() {
    this._searchToken++;
    this._refresh();
  }
}

customElements.define("school-search", SchoolSearch);
